import React, { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { useApiGet, useApiSend } from '../../../utils/httpClientTs';
import {
  createEvent,
  getEventScopes,
  getHubEvents,
} from '../../../endpoints/eventsTs';
import { EventScopeOption } from '../../../types/events';
import { eventType } from '../../../utils/variables';
import EventsMonthCalendar, {
  calendarGridRange,
} from '../LocalChapter/EventsMonthCalendar';

function scopeKey(option: { kind: string; id: string }): string {
  return `${option.kind}:${option.id || ''}`;
}

const EventsHubPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const localChapterFromCard = searchParams.get('local_chapter_id') || '';
  const kind =
    searchParams.get('kind') || (localChapterFromCard ? 'local' : 'all');
  const id = searchParams.get('id') || localChapterFromCard;
  const [month, setMonth] = useState<Dayjs>(() => dayjs().startOf('month'));
  const [createOpen, setCreateOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [typeId, setTypeId] = useState(eventType.Meeting);
  const [starts, setStarts] = useState<Dayjs | null>(
    dayjs().add(1, 'hour').startOf('hour')
  );
  const [ends, setEnds] = useState<Dayjs | null>(
    dayjs().add(2, 'hour').startOf('hour')
  );
  const [createError, setCreateError] = useState('');

  const { data: scopes } = useApiGet(['event-scopes'], getEventScopes, {
    retry: 1,
  });
  const options = scopes?.options ?? [];
  const selected = options.find(
    (option) => option.kind === kind && (option.id || '') === id
  );

  const range = useMemo(() => calendarGridRange(month), [month]);
  const timezoneOffset = useMemo(
    () => -new Date().getTimezoneOffset() / 60,
    []
  );
  const {
    data: events,
    isFetching,
    refetch,
  } = useApiGet(
    [
      'hub-events',
      range.start.format('YYYY-MM-DD'),
      range.end.format('YYYY-MM-DD'),
      kind,
      id,
      timezoneOffset,
    ],
    () =>
      getHubEvents({
        startDate: range.start.format('YYYY-MM-DD'),
        endDate: range.end.format('YYYY-MM-DD'),
        timezoneOffset,
        hubKind: kind,
        hubId: id,
      }),
    { retry: 1 }
  );

  const choose = (option: EventScopeOption) => {
    const next = new URLSearchParams(searchParams);
    next.delete('local_chapter_id');
    if (option.kind === 'all') {
      next.delete('kind');
      next.delete('id');
    } else {
      next.set('kind', option.kind);
      if (option.id) {
        next.set('id', option.id);
      } else {
        next.delete('id');
      }
    }
    setSearchParams(next, { replace: true });
  };

  const { mutate: save, isPending } = useApiSend(
    () => {
      const payload: {
        title: string;
        event_type_id: string;
        meeting_on: string;
        ending_on: string;
        local_chapter_id?: string;
        spd_chapter_id?: string;
        group_id?: string;
      } = {
        title: title.trim(),
        event_type_id: typeId,
        meeting_on: starts!.toISOString(),
        ending_on: ends!.toISOString(),
      };
      if (selected?.kind === 'local') {
        payload.local_chapter_id = selected.id;
      } else if (selected?.kind === 'spd') {
        payload.spd_chapter_id = selected.id;
      } else if (selected?.id) {
        payload.group_id = selected.id;
      }
      return createEvent(payload);
    },
    (created) => {
      setCreateOpen(false);
      refetch();
      if (created?.id) {
        navigate(`/events/${created.id}`);
      }
    },
    (err: Error) =>
      setCreateError(err?.message || 'Could not create the event.')
  );

  const openCreate = (day?: Dayjs) => {
    const start = (day || dayjs()).hour(18).minute(0).second(0);
    setStarts(start);
    setEnds(start.add(1, 'hour'));
    setTitle('');
    setCreateError('');
    setCreateOpen(true);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        alignItems={{ sm: 'center' }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="h5">Events</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 240 }}>
            <InputLabel id="event-scope-label">Show</InputLabel>
            <Select
              labelId="event-scope-label"
              label="Show"
              value={scopeKey({ kind, id })}
              onChange={(event) => {
                const option = options.find(
                  (item) => scopeKey(item) === event.target.value
                );
                if (option) choose(option);
              }}
            >
              {!options.some(
                (option) => scopeKey(option) === scopeKey({ kind, id })
              ) && (
                <MenuItem value={scopeKey({ kind, id })}>
                  {kind === 'all' ? 'All I can see' : 'Events'}
                </MenuItem>
              )}
              {options.map((option) => (
                <MenuItem key={scopeKey(option)} value={scopeKey(option)}>
                  {option.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {selected?.can_create && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openCreate()}
            >
              Create
            </Button>
          )}
        </Stack>
      </Stack>
      {isFetching && !events && (
        <Typography color="text.secondary">Loading events...</Typography>
      )}
      <EventsMonthCalendar
        month={month}
        events={events || []}
        canEditEvents={Boolean(selected?.can_create)}
        onMonthChange={(next) => setMonth(next.startOf('month'))}
        onEventClick={(eventId) => navigate(`/events/${eventId}`)}
        onEmptyDayClick={(day) => {
          if (selected?.can_create) openCreate(day);
        }}
      />
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} fullWidth>
        <DialogTitle>New event</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {createError && <Alert severity="error">{createError}</Alert>}
            <FormControl fullWidth size="small">
              <InputLabel id="event-type-label">Type</InputLabel>
              <Select
                labelId="event-type-label"
                label="Type"
                value={typeId}
                onChange={(event) => setTypeId(event.target.value)}
              >
                <MenuItem value={eventType.Meeting}>Meeting</MenuItem>
                <MenuItem value={eventType.Social}>Social</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              fullWidth
              size="small"
              required
            />
            <DateTimePicker
              label="Starts"
              value={starts}
              onChange={(value) => {
                setStarts(value);
                if (value && (!ends || !ends.isAfter(value))) {
                  setEnds(value.add(1, 'hour'));
                }
              }}
            />
            <DateTimePicker label="Ends" value={ends} onChange={setEnds} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={isPending || !title.trim() || !starts || !ends}
            onClick={() => save(undefined)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EventsHubPage;
